import type { TranslationBundle } from '@bms/common';

/** Front-desk card issuing screen. */
export const cardDesk: TranslationBundle = {
  en: {

    // CardsPage — the site-wide card desk
    noSiteTitle: 'No site selected',
    noSiteDescription: 'Choose a site to see the cards issued at it.',
    notAvailableTitle: 'Cards is not available to this account',
    notAvailableDescription: 'This account cannot see cards at this site. Ask a site administrator for access.',

    chipUnknownAria: 'Chip type unknown',
    chipAria: 'Chip {{name}}',
    issueRequiresWrite: 'Issuing a card requires the cards:write permission',

    toastCardSuspended: 'Card suspended',
    toastSuspendFailed: 'Failed to suspend the card',
    toastCardReactivated: 'Card reactivated',
    toastReactivateFailed: 'Failed to reactivate the card',

    columnChip: 'Chip',
    columnCard: 'Card',
    columnHolder: 'Holder',
    columnValidUntil: 'Valid until',
    columnStatus: 'Status',
    noExpiry: 'No expiry',

    actionSuspend: 'Suspend',
    actionReactivate: 'Reactivate',
    actionRevoke: 'Revoke',
    actionReplace: 'Replace',
    actionsForCard: 'Actions for {{uid}}',

    searchHolderPlaceholder: 'Holder name',
    searchHolderAria: 'Search by holder name',
    searchUidPlaceholder: 'Card UID (exact)',
    searchUidAria: 'Search by card UID, matched exactly',
    filterTypeAria: 'Filter by card type',
    filterStatusAria: 'Filter by status',
    anyType: 'Any type',
    anyStatus: 'Any status',
    issueStaffCard: 'Issue staff card',
    allCards: 'All cards',
    guestKeys: 'Active guest keys',
    roomGroup: 'Room',
    noRoomGroup: 'No room',

    tableAria: 'Cards',
    noMatchesTitle: 'No cards match these filters',
    noMatchesDescription: 'A card UID has to be typed in full — it is matched exactly, not as a prefix.',
    noGuestKeysTitle: 'No active guest keys',
    noGuestKeysDescription: "A guest's keys appear here under their room once they are written, at check-in or with Add key.",
    noCardsTitle: 'No cards yet',
    noCardsDescription: 'Cards issued at this site appear here. Issue a staff card, or issue a guest card from a room.',
    truncatedNotice: 'Showing the {{pageSize}} most recent cards that match. Narrow the search to be sure you are seeing the one you want.',

    statusOptionPending: 'pending',
    statusOptionActive: 'active',
    statusOptionSuspended: 'suspended',
    statusOptionRevoked: 'revoked',
    statusOptionNotYetValid: 'not yet valid',
    statusOptionExpired: 'expired',

    revokeTitle: 'Revoke {{uid}}',
    revokeDescription: 'A revoked card is never reactivated, and the revocation is published to every gateway in the site so the door denies it even while it cannot reach Edge. The holder gets a new card through replacement, which keeps the audit chain.',
    reasonLabel: 'Reason',
    reasonHint: 'Stored on the record and published with the revocation.',
    lostHint: 'A lost report writes the <0>lost_reported</0> event as well, so the trail says the card went missing rather than that someone withdrew it.',
    revokeCardButton: 'Revoke card',
    toastLostRevoked: 'Card reported lost and revoked',
    toastRevoked: 'Card revoked',
    toastRevokeFailed: 'Failed to revoke the card',

    detailTitle: 'Card detail',
    fieldCardUid: 'Card UID',
    fieldType: 'Type',
    fieldChip: 'Chip',
    fieldStatus: 'Status',
    fieldHolder: 'Holder',
    fieldRole: 'Role',
    fieldRoom: 'Room',
    fieldValidFrom: 'Valid from',
    fieldValidUntil: 'Valid until',
    fieldIssuedBy: 'Issued by',
    fieldReplaces: 'Replaces',
    fieldReplacedBy: 'Replaced by',
    fieldRevokedBy: 'Revoked by',
    fieldRevokedAt: 'Revoked at',
    fieldRevokeReason: 'Revoke reason',
    revokeReasonWord: {
      lost: 'Lost', stolen: 'Stolen', manual: 'Withdrawn by staff', checkout: 'Guest checked out',
      replaced: 'Replaced by a new card', other: 'Other reason',
    },
    revokeWithReason: 'Revoke with reason',
    eventHistoryTitle: 'Event history',
    noEvents: 'No events.',
    newCardPrefix: 'New card {{uid}}',

    eventIssued: 'Issued',
    eventActivated: 'Activated',
    eventSuspended: 'Suspended',
    eventReactivated: 'Reactivated',
    eventRevoked: 'Revoked',
    eventReplaced: 'Replaced',
    eventWrittenToCard: 'Written to card',
    eventLostReported: 'Lost reported',
    eventAclUpdated: 'ACL updated',
    eventRetired: 'Retired',

    reEncodeFinish: 'Finish writing',
    reEncodeAgain: 'Write again',
    reEncodePlaceCard: 'Place the card on the reader first',
    reEncodeUnsupportedCarrier: 'The card on the reader is not a supported ULAES or EV2 carrier',
    reEncodeNotWritten: 'The card was not written: {{reason}}',
    reEncodeNoReport: 'The encoder did not return a completed write report',
    reEncodeUnknownWarning: 'The encoder could not confirm the write to {{uid}}; Edge recorded the card as pending. Do not hand it out until Finish writing succeeds.',
    reEncodeWrittenConfirmed: 'Card {{uid}} written and confirmed',
    reEncodeConfirmFailed: 'The card is written but Edge did not save it. Keep the card here and retry Save; do not encode it again or hand it out yet.',
    reEncodeWriteFailed: 'Failed to write the card',
    reEncodeSaveTitle: 'Save {{uid}} in Edge?',
    reEncodeWriteTitle: 'Write {{uid}} again?',
    reEncodeSaveDescription: 'The physical write succeeded. Retry only the Edge save; do not encode the card again.',
    reEncodeWriteDescription: 'The chip on the reader is written with this card’s stored access and its existing signed window. The record keeps its identity and its history — this issues nothing new.',
    reEncodeWarning: 'Make sure the chip on the reader is the one that belongs to this record. Writing a different chip gives two chips the same identity, and the audit trail cannot tell them apart.',
    reEncodeSaving: 'Saving…',
    reEncodeWriting: 'Writing…',
    reEncodeRetrySave: 'Retry save',
    reEncodeWriteCard: 'Write the card',

    // frontOfficeView.ts — cardsSummary (a pure view model; takes
    // `tr: Translate = english`, mirroring Admin's model/translate.ts). The
    // total is invariant text rather than a plural form on purpose: the
    // existing English reads "1 cards" and `frontOfficeView.test.ts` pins it.
    frontOfficeSummaryTotal_one: '{{count}} card',
    frontOfficeSummaryTotal_other: '{{count}} cards',
    frontOfficeSummaryActive_one: '{{count}} active',
    frontOfficeSummaryActive_other: '{{count}} active',
    frontOfficeSummaryPending_one: '{{count}} awaiting a write',
    frontOfficeSummaryPending_other: '{{count}} awaiting a write',
    frontOfficeSummaryRevoked_one: '{{count}} revoked',
    frontOfficeSummaryRevoked_other: '{{count}} revoked',
  },
  hr: {

    // CardsPage — kartični pult lokacije
    noSiteTitle: 'Nije odabrana lokacija',
    noSiteDescription: 'Odaberite lokaciju da biste vidjeli izdane kartice.',
    notAvailableTitle: 'Kartice nisu dostupne ovom računu',
    notAvailableDescription: 'Ovaj račun na ovoj lokaciji ne može vidjeti kartice. Zamolite administratora lokacije za pristup.',

    chipUnknownAria: 'Vrsta čipa nepoznata',
    chipAria: 'Čip {{name}}',
    issueRequiresWrite: 'Izdavanje kartice zahtijeva ovlast cards:write',

    toastCardSuspended: 'Kartica suspendirana',
    toastSuspendFailed: 'Suspenzija kartice nije uspjela',
    toastCardReactivated: 'Kartica ponovno aktivirana',
    toastReactivateFailed: 'Ponovna aktivacija kartice nije uspjela',

    columnChip: 'Čip',
    columnCard: 'Kartica',
    columnHolder: 'Nositelj',
    columnValidUntil: 'Vrijedi do',
    columnStatus: 'Status',
    noExpiry: 'Bez isteka',

    actionSuspend: 'Suspendiraj',
    actionReactivate: 'Ponovno aktiviraj',
    actionRevoke: 'Opozovi',
    actionReplace: 'Zamijeni',
    actionsForCard: 'Radnje za {{uid}}',

    searchHolderPlaceholder: 'Ime nositelja',
    searchHolderAria: 'Pretražite po imenu nositelja',
    searchUidPlaceholder: 'UID kartice (točno)',
    searchUidAria: 'Pretražite po UID-u kartice, točno podudaranje',
    filterTypeAria: 'Filtriraj po vrsti kartice',
    filterStatusAria: 'Filtriraj po statusu',
    anyType: 'Bilo koja vrsta',
    anyStatus: 'Bilo koji status',
    issueStaffCard: 'Izdaj karticu osoblja',
    allCards: 'Sve kartice',
    guestKeys: 'Aktivni ključevi gostiju',
    roomGroup: 'Soba',
    noRoomGroup: 'Bez sobe',

    tableAria: 'Kartice',
    noMatchesTitle: 'Nijedna kartica ne odgovara ovim filtrima',
    noMatchesDescription: 'UID kartice mora biti upisan u cijelosti — podudara se točno, ne kao prefiks.',
    noGuestKeysTitle: 'Nema aktivnih ključeva gostiju',
    noGuestKeysDescription: 'Ključevi gosta pojavljuju se ovdje, pod njegovom sobom, čim se zapišu pri prijavi ili s Dodaj ključ.',
    noCardsTitle: 'Još nema kartica',
    noCardsDescription: 'Kartice izdane na ovoj lokaciji pojavljuju se ovdje. Izdajte karticu osoblja ili izdajte gostinjsku karticu iz sobe.',
    truncatedNotice: 'Prikazano {{pageSize}} najnovijih kartica koje odgovaraju. Suzite pretragu kako biste bili sigurni da vidite pravu.',

    statusOptionPending: 'na čekanju',
    statusOptionActive: 'aktivna',
    statusOptionSuspended: 'suspendirana',
    statusOptionRevoked: 'opozvana',
    statusOptionNotYetValid: 'još ne vrijedi',
    statusOptionExpired: 'istekla',

    revokeTitle: 'Opozovi {{uid}}',
    revokeDescription: 'Opozvana kartica se nikad ponovno ne aktivira, a opoziv se objavljuje svakom pristupniku na lokaciji, tako da vrata odbijaju karticu čak i dok ne mogu doseći Edge. Nositelj dobiva novu karticu putem zamjene, čime se čuva revizijski slijed.',
    reasonLabel: 'Razlog',
    reasonHint: 'Pohranjuje se uz zapis i objavljuje s opozivom.',
    lostHint: 'Prijava gubitka dodatno upisuje događaj <0>lost_reported</0>, tako da trag pokazuje da je kartica izgubljena, a ne da ju je netko povukao.',
    revokeCardButton: 'Opozovi karticu',
    toastLostRevoked: 'Kartica prijavljena kao izgubljena i opozvana',
    toastRevoked: 'Kartica opozvana',
    toastRevokeFailed: 'Opoziv kartice nije uspio',

    detailTitle: 'Detalji kartice',
    fieldCardUid: 'UID kartice',
    fieldType: 'Vrsta',
    fieldChip: 'Čip',
    fieldStatus: 'Status',
    fieldHolder: 'Nositelj',
    fieldRole: 'Uloga',
    fieldRoom: 'Soba',
    fieldValidFrom: 'Vrijedi od',
    fieldValidUntil: 'Vrijedi do',
    fieldIssuedBy: 'Izdao/la',
    fieldReplaces: 'Zamjenjuje',
    fieldReplacedBy: 'Zamijenjena s',
    fieldRevokedBy: 'Opozvao/la',
    fieldRevokedAt: 'Opozvana',
    fieldRevokeReason: 'Razlog opoziva',
    revokeReasonWord: {
      lost: 'Izgubljena', stolen: 'Ukradena', manual: 'Povuklo osoblje', checkout: 'Gost se odjavio',
      replaced: 'Zamijenjena novom karticom', other: 'Drugi razlog',
    },
    revokeWithReason: 'Opozovi uz razlog',
    eventHistoryTitle: 'Povijest događaja',
    noEvents: 'Nema događaja.',
    newCardPrefix: 'Nova kartica {{uid}}',

    eventIssued: 'Izdana',
    eventActivated: 'Aktivirana',
    eventSuspended: 'Suspendirana',
    eventReactivated: 'Ponovno aktivirana',
    eventRevoked: 'Opozvana',
    eventReplaced: 'Zamijenjena',
    eventWrittenToCard: 'Zapisano na karticu',
    eventLostReported: 'Prijavljen gubitak',
    eventAclUpdated: 'ACL ažuriran',
    eventRetired: 'Umirovljena',

    reEncodeFinish: 'Dovrši zapisivanje',
    reEncodeAgain: 'Zapiši ponovno',
    reEncodePlaceCard: 'Prvo stavite karticu na čitač',
    reEncodeUnsupportedCarrier: 'Kartica na čitaču nije podržani ULAES ili EV2 nosač',
    reEncodeNotWritten: 'Kartica nije zapisana: {{reason}}',
    reEncodeNoReport: 'Enkoder nije vratio dovršeno izvješće o zapisivanju',
    reEncodeUnknownWarning: 'Enkoder nije mogao potvrditi zapis na {{uid}}; Edge je karticu zabilježio kao na čekanju. Ne izdajte je dok Dovrši zapisivanje ne uspije.',
    reEncodeWrittenConfirmed: 'Kartica {{uid}} zapisana i potvrđena',
    reEncodeConfirmFailed: 'Kartica je zapisana, ali je Edge nije spremio. Zadržite karticu ovdje i ponovno pokušajte Spremi; ne zapisujte je ponovno niti je izdajte.',
    reEncodeWriteFailed: 'Zapisivanje kartice nije uspjelo',
    reEncodeSaveTitle: 'Spremiti {{uid}} u Edge?',
    reEncodeWriteTitle: 'Zapisati {{uid}} ponovno?',
    reEncodeSaveDescription: 'Fizičko zapisivanje je uspjelo. Ponovite samo spremanje u Edge; ne zapisujte karticu ponovno.',
    reEncodeWriteDescription: 'Čip na čitaču zapisuje se s pohranjenim pristupom ove kartice i njezinim postojećim potpisanim razdobljem. Zapis zadržava svoj identitet i povijest — ovo ne izdaje ništa novo.',
    reEncodeWarning: 'Provjerite da je čip na čitaču upravo onaj koji pripada ovom zapisu. Zapisivanje drugog čipa daje dvama čipovima isti identitet, a revizijski trag ih ne može razlikovati.',
    reEncodeSaving: 'Spremanje…',
    reEncodeWriting: 'Zapisivanje…',
    reEncodeRetrySave: 'Ponovi spremanje',
    reEncodeWriteCard: 'Zapiši karticu',

    // frontOfficeView.ts — cardsSummary
    frontOfficeSummaryTotal_one: '{{count}} kartica',
    frontOfficeSummaryTotal_few: '{{count}} kartice',
    frontOfficeSummaryTotal_other: '{{count}} kartica',
    frontOfficeSummaryActive_one: '{{count}} aktivna',
    frontOfficeSummaryActive_few: '{{count}} aktivne',
    frontOfficeSummaryActive_other: '{{count}} aktivnih',
    frontOfficeSummaryPending_one: '{{count}} čeka zapisivanje',
    frontOfficeSummaryPending_few: '{{count}} čekaju zapisivanje',
    frontOfficeSummaryPending_other: '{{count}} čeka zapisivanje',
    frontOfficeSummaryRevoked_one: '{{count}} opozvana',
    frontOfficeSummaryRevoked_few: '{{count}} opozvane',
    frontOfficeSummaryRevoked_other: '{{count}} opozvanih',
  },
};
